import React from 'react';

type IconProps = {
  className?: string;
};

/**
 * Same visual language as the original icon set (Icons.tsx / Icons2.tsx):
 * - Extruded "chip" body: offset dark grey shape behind a metallic grey gradient top face.
 * - Accent (#F5401F) elements use their own warm gradient plus a small radial gloss
 *   highlight, so they read as a glossy/lacquered bead against the neutral metal.
 * - Soft black drop shadow lifts the whole icon off the page.
 */

export const Icons4 = {
  LearnerReadiness: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="lr-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="lr-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="lr-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="lr-gloss" cx="0.32" cy="0.26" r="0.6">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="lr-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#lr-shadow)">
        {/* extruded tile */}
        <rect x="20" y="24" width="80" height="80" rx="20" fill="url(#lr-side)" />
        <rect x="18" y="20" width="80" height="80" rx="20" fill="url(#lr-top)" />
        <rect x="18" y="20" width="80" height="26" rx="20" fill="#ffffff" opacity="0.1" />

        {/* extruded trend line, thick tube with round joints */}
        <polyline
          points="34,78 50,62 60,70 82,42"
          fill="none"
          stroke="#7a1a06"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.4"
          transform="translate(0,3)"
        />
        <polyline
          points="34,78 50,62 60,70 82,42"
          fill="none"
          stroke="url(#lr-accent)"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points="34,78 50,62 60,70 82,42"
          fill="none"
          stroke="#ffffff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.3"
        />

        {/* arrowhead, extruded */}
        <polygon points="82,42 68,40 78,52" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <polygon points="82,42 68,40 78,52" fill="url(#lr-accent)" />

        {/* endpoint beads */}
        <circle cx="34" cy="78" r="5" fill="url(#lr-accent)" />
        <circle cx="60" cy="70" r="5" fill="url(#lr-accent)" />
        <ellipse cx="57.5" cy="67.5" rx="1.8" ry="1.4" fill="url(#lr-gloss)" />
      </g>
    </svg>
  ),

  FasterOnboarding: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="fo-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="fo-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="fo-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="fo-gloss" cx="0.34" cy="0.28" r="0.7">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="fo-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#fo-shadow)">
        <rect x="20" y="24" width="80" height="80" rx="20" fill="url(#fo-side)" />
        <rect x="18" y="20" width="80" height="80" rx="20" fill="url(#fo-top)" />
        <rect x="18" y="20" width="80" height="26" rx="20" fill="#ffffff" opacity="0.1" />

        {/* extruded clock face, with a small motion-tick to suggest "faster" */}
        <circle cx="58" cy="60" r="26" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <circle cx="58" cy="60" r="26" fill="url(#fo-accent)" />
        <circle cx="58" cy="60" r="19" fill="#d9300c" opacity="0.55" />
        <ellipse cx="49" cy="50" rx="8" ry="6" fill="url(#fo-gloss)" />

        <g stroke="#ffffff" strokeWidth="3.4" strokeLinecap="round">
          <line x1="58" y1="60" x2="58" y2="47" />
          <line x1="58" y1="60" x2="67" y2="63" />
        </g>
        <circle cx="58" cy="60" r="2.6" fill="#ffffff" />

        {/* speed lines trailing the rim, implying "faster" */}
        <g stroke="url(#fo-accent)" strokeWidth="3" strokeLinecap="round" opacity="0.9">
          <line x1="88" y1="52" x2="96" y2="48" />
          <line x1="90" y1="60" x2="99" y2="60" />
          <line x1="88" y1="68" x2="96" y2="72" />
        </g>
      </g>
    </svg>
  ),

  BusinessOutcomeTracking: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bo-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="bo-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="bo-accent" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="bo-gloss" cx="0.3" cy="0.25" r="0.6">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="bo-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#bo-shadow)">
        <rect x="20" y="24" width="80" height="80" rx="20" fill="url(#bo-side)" />
        <rect x="18" y="20" width="80" height="80" rx="20" fill="url(#bo-top)" />
        <rect x="18" y="20" width="80" height="26" rx="20" fill="#ffffff" opacity="0.1" />

        {/* baseline */}
        <line x1="34" y1="80" x2="84" y2="80" stroke="#2b2b2b" strokeWidth="2.4" opacity="0.4" />

        {/* three extruded 3D bars, ascending */}
        <g>
          <rect x="38" y="62" width="12" height="18" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
          <rect x="38" y="60" width="12" height="20" rx="2" fill="url(#bo-accent)" />
          <rect x="38" y="60" width="12" height="5" rx="2" fill="#ffffff" opacity="0.3" />
        </g>
        <g>
          <rect x="53" y="46" width="12" height="34" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
          <rect x="53" y="44" width="12" height="36" rx="2" fill="url(#bo-accent)" />
          <rect x="53" y="44" width="12" height="5" rx="2" fill="#ffffff" opacity="0.3" />
        </g>
        <g>
          <rect x="68" y="34" width="12" height="46" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
          <rect x="68" y="32" width="12" height="48" rx="2" fill="url(#bo-accent)" />
          <rect x="68" y="32" width="12" height="5" rx="2" fill="#ffffff" opacity="0.3" />
        </g>
        <ellipse cx="72" cy="38" rx="3.2" ry="2.2" fill="url(#bo-gloss)" />
      </g>
    </svg>
  ),
};

export default Icons4;
